<template>
	<CSettingsSection
		:loading="loading"
		loader-type="table"
		:loading-size="2"
		class="organization-email-domains"
	>
		<!-- Callouts and Instructions -->
		<CCallout v-if="model.EmailDomainsAreLocked" type="warning" class="my-4">
			You must upgrade either to the Team or Enterprise plan to re-enable your custom email domains.
		</CCallout>
		<template v-else>
			<CCallout v-if="hasUnverifiedDomains" type="warning" class="my-4">
				You have domains that require DNS configuration. We cannot send emails on behalf of unverified domains.
				<a href="/support/244/building-forms/verified-email-domains" target="_blank">Learn more.</a>
			</CCallout>
			<p v-if="model.EmailDomains.length > 0" class="my-4">
				Your organization has added {{ model.EmailDomains.length }} out of {{ model.EmailDomainsLimit }} email domains included on your {{ model.BillingPlan }} plan.
			</p>
			<template v-else>
				<CCallout type="info" class="my-4">
					<template v-if="model.EmailDomainsLimit > 0">
						Send emails on behalf of your website domain. Verify up to two domain names and hide any mention of Cognito Forms. <a href="/features/email-notifications#use-your-own-email-address" target="_blank">Learn more</a>.
					</template>
					<template v-else>
						Organizations on the Team and Enterprise plans can verify up to two email domains to allow us to send notification and confirmation emails on behalf of your domain&mdash;without any mention of Cognito Forms.
						<RouterLink :to="`/${orgCode}/settings/plan/select?source=feature&details=users`">Upgrade&nbsp;now</RouterLink>.
					</template>
				</CCallout>
			</template>
		</template>

		<!-- Email Domains List Table -->
		<ul v-show="model.EmailDomains.length" class="divide-y divide-shade-3 border-table">
			<li
				v-for="domain of model.EmailDomains"
				:key="domain.Id"
				:class="{ 'locked': model.EmailDomainsAreLocked }"
				class="p-3 bg-transparent odd:bg-shade-2 flex items-center gap-2"
			>
				<!-- Verified domain -->
				<template v-if="domain.IsVerified">
					<strong class="truncate">{{ domain.Id }}</strong>
					<CBadge text="Verified" type="success" data-testid="verified-domain" />
				</template>
				<!-- Unverified domain shows verify button and allows clicking on domain name to open settings -->
				<template v-else>
					<strong class="cursor-pointer truncate" @click="$emit('domain-settings', domain)">{{ domain.Id }}</strong>
					<template v-if="!model.EmailDomainsAreLocked">
						<CToolTip
							placement="top"
							:use-button="false"
							is-admin
							popper-class="tooltip"
						>
							<ul class="-my-3">
								<li v-for="item of domainVerificationChecklist(domain)" :key="item.label" class="my-3">
									<Inline v-if="item.value" svg="check" class="text-icon-success" />
									<Inline v-else svg="ex" class="text-red-4" />
									{{ item.label }}
								</li>
							</ul>
							<template #button>
								<CBadge
									:text="domain.Id === verifyingDomain ? 'Verifying' : 'Unverified'"
									type="warning"
									data-testid="unverified-domain"
								/>
							</template>
						</CToolTip>

						<CToolTip placement="top" :open-delay="600" :use-button="false">
							<template #button>
								<CIconButton
									icon="sync"
									class="p-2"
									:class="{ 'spin': domain.Id === verifyingDomain }"
									aria-label="Verify Email Domain"
									data-testid="verify-email-domain"
									@click="$emit('verify-domain', domain)"
								/>
							</template>
							<span>Verify Email Domain</span>
						</CToolTip>
					</template>
				</template>

				<div class="ml-auto flex gap-4">
					<!-- Show Email Domain Settings button -->
					<CToolTip placement="top" :open-delay="600" :use-button="false">
						<template #button>
							<CIconButton
								v-if="!domain.IsVerified && !model.EmailDomainsAreLocked"
								icon="settings"
								class="p-2"
								aria-label="Domain Settings"
								data-testid="domain-settings"
								@click="$emit('domain-settings', domain)"
							/>
						</template>
						<span>Domain Settings</span>
					</CToolTip>

					<!-- Delete Email Domain button -->
					<CToolTip placement="top" :open-delay="600" :use-button="false">
						<template #button>
							<CIconButton
								icon="trash"
								class="p-2"
								aria-label="Delete Email Domain"
								data-testid="delete-email-domain"
								@click="$emit('delete-domain', domain)"
							/>
						</template>
						<span>Delete Email Domain</span>
					</CToolTip>
				</div>
			</li>
		</ul>
	</CSettingsSection>
</template>

<script lang="ts">
	import { defineComponent, computed } from 'vue';
	import type { PropType } from 'vue';
	import type { EmailDomainSettingsViewModel } from '@cognitoforms/types/server-types/model/email-domain-settings-view-model';
	import type { EmailDomain } from '@cognitoforms/types/server-types/model/email-domain';
	import CToolTip from 'src/components/interaction/ToolTip.vue';
	import CSettingsSection from './SettingsSection.vue';
	import CCallout from 'src/components/content/Callout.vue';
	import CBadge from 'src/components/info/Badge.vue';
	import CIconButton from 'src/components/interaction/IconButton.vue';

	export default defineComponent({
		name: 'OrganizationEmailDomainsView',
		components: {
			CToolTip,
			CSettingsSection,
			CCallout,
			CBadge,
			CIconButton
		},
		props: {
			model: { type: Object as PropType<EmailDomainSettingsViewModel>, required: true },
			orgCode: { type: String, required: true },
			loading: { type: Boolean, default: true },
			verifyingDomain: { type: String, default: '' }
		},
		emit: ['verify-domain', 'domain-settings', 'add-domain', 'delete-domain'],
		setup(props) {
			return {
				hasUnverifiedDomains: computed(() => {
					return props.model.EmailDomains.some((d: EmailDomain) => !d.IsVerified);
				}),
				domainVerificationChecklist(domain: EmailDomain) {
					const checklist: Array<{label: string, value: boolean}> = [];
					for (const p of domain.Providers) {
						checklist.push({
							label: `${p.Name} DKIM`,
							value: p.DkimIsValid
						});
					}
					checklist.push({
						label: 'SPF',
						value: domain.SpfIsValid
					});
					return checklist;
				}
			};
		}
	});
</script>